import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { theme } from "@/styles/theme";
import { media } from "@/styles/media";
import Header from "@/components/main/Header";
import Banner from "@/components/learn/Banner";
import ContactUs from "@/components/contact-us";
import Footer from "@/components/main/Footer";

export default function Page() {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const contentsRef = useRef<HTMLDivElement[]>([]);
  const navRef = useRef<HTMLDivElement>(null);
  const navItemsRef = useRef<HTMLParagraphElement[]>([]);
  const navGaugeRef = useRef<HTMLDivElement>(null);

  const [currIdx, setCurrIdx] = useState<number>(0);

  useEffect(() => {
    if (!contentRef.current || !navRef.current || !navItemsRef.current || !navGaugeRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleHeadings = entries.filter((entry) => entry.isIntersecting);
        if (visibleHeadings.length < 1) return;

        const newCurrIdx = Number(visibleHeadings[0].target.getAttribute("idx"));
        setCurrIdx(newCurrIdx);
      },
      {
        root: null,
      }
    );

    contentsRef.current = [...contentRef.current.querySelectorAll("& > div")] as HTMLDivElement[];
    navItemsRef.current = [...navRef.current.querySelectorAll("& > p")] as HTMLParagraphElement[];
    contentsRef.current.forEach((v, i) => {
      v.setAttribute("idx", String(i));
      observer.observe(v);
    });
  }, []);

  useEffect(() => {
    if (!navItemsRef.current || !navGaugeRef.current) return;

    navGaugeRef.current.style.top = `${navItemsRef.current.slice(0, currIdx).reduce((prev, curr) => {
      return prev + curr.clientHeight;
    }, 0)}px`;
    navGaugeRef.current.style.height = `${navItemsRef.current[currIdx].clientHeight}px`;
  }, [currIdx]);

  const items = [
    {
      title: t("menu.learn.inbody-in-studies.item-1.title"),
      img: "/learn/study/Study-1.webp",
      desc: t("menu.learn.inbody-in-studies.item-1.desc"),
    },
    {
      title: t("menu.learn.inbody-in-studies.item-2.title"),
      img: "/learn/study/Study-3.webp",
      desc: t("menu.learn.inbody-in-studies.item-2.desc"),
    },
    {
      title: t("menu.learn.inbody-in-studies.item-3.title"),
      img: "/learn/study/Study-4.webp",
      desc: t("menu.learn.inbody-in-studies.item-3.desc"),
    },
    {
      title: t("menu.learn.inbody-in-studies.item-4.title"),
      img: "/learn/study/Study-5.webp",
      desc: t("menu.learn.inbody-in-studies.item-4.desc"),
    },
    {
      title: t("menu.learn.inbody-in-studies.item-5.title"),
      img: "/learn/study/Track_Body_Composition.webp",
      desc: t("menu.learn.inbody-in-studies.item-5.desc"),
    },
    {
      title: t("menu.learn.inbody-in-studies.item-6.title"),
      img: "/learn/study/High_Agreement_with_D20.webp",
      desc: t("menu.learn.inbody-in-studies.item-6.desc"),
    },
    {
      title: t("menu.learn.inbody-in-studies.item-7.title"),
      img: "/learn/study/Study-8.webp",
      desc: t("menu.learn.inbody-in-studies.item-7.desc"),
    },
    {
      title: t("menu.learn.inbody-in-studies.item-8.title"),
      img: "/learn/study/Study-9.webp",
      desc: t("menu.learn.inbody-in-studies.item-8.desc"),
    },
  ];

  return (
    <>
      <Header />
      <Banner
        Bg="/learn/banner/InBody_in_Studies-Banner.webp"
        Headline="Learn"
        Title="InBody in Studies"
        Desc="Over 6,000 research studies utilizing the InBody"
      />

      <Title>{t("menu.learn.inbody-in-studies.title")}</Title>

      <Wrap>
        <Nav>
          <NavContent>
            <NavGaugeWrap>
              <NavGaugeRail />
              <NavGauge ref={navGaugeRef} />
            </NavGaugeWrap>
            <NavItems ref={navRef}>
              {items.map((v, i) => (
                <NavItem
                  key={v.title}
                  $active={i === currIdx}
                  onClick={() => {
                    window.scrollTo({
                      top: contentsRef.current[i].offsetTop - 120,
                      behavior: "smooth",
                    });
                  }}
                >{`${t("menu.learn.inbody-in-studies.study")} ${i + 1} | ${v.title}`}</NavItem>
              ))}
            </NavItems>
          </NavContent>
        </Nav>

        <Content ref={contentRef}>
          {items.map((v, i) => (
            <ContentItem key={v.title} className="observer-content">
              <ContentSubTitle
                data-aos="fade-down"
                data-aos-easing="ease-out"
                data-aos-delay="200"
                data-aos-duration="500"
              >{`${t("menu.learn.inbody-in-studies.study")} ${i + 1}`}</ContentSubTitle>
              <ContentTitle data-aos="fade-up" data-aos-easing="ease-out" data-aos-delay="200" data-aos-duration="500">
                {v.title}
              </ContentTitle>
              <ContentImg src={v.img} />
              <ContentDesc dangerouslySetInnerHTML={{ __html: v.desc }} />
            </ContentItem>
          ))}
        </Content>
      </Wrap>
      <ContactUs />
      <Footer />
    </>
  );
}

const Wrap = styled.div`
  display: flex;
`;
const Title = styled.p`
  margin: 30px 0 0 0;
  padding: 0 0 0 50px;
  max-width: 500px;
  line-height: 62px;
  font-family: "Lato Bold", sans-serif;
  font-size: 38px;
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.primary};
  ${media.tablet`
    padding: 0 20px;
  `}
`;

const Nav = styled.div`
  position: sticky;
  display: block;
  padding: 0 0 0 50px;
  top: 75px;
  left: 0;
  min-width: 225px;
  width: 30%;
  height: 100%;
  ${media.tablet`
    display: none;
  `}
`;
const NavContent = styled.div`
  display: flex;
  margin: 20px 0;
`;
const NavGaugeWrap = styled.div`
  position: relative;
`;
const NavGaugeRail = styled.div`
  display: block;
  background-color: rgba(0, 0, 0, 0.1);
  width: 1px;
  height: 100%;
`;
const NavGauge = styled.div`
  position: absolute;
  top: 0;
  left: -1px;
  width: 3px;
  height: 30px;
  background-color: ${theme.colors.primary};
  transition-duration: 100ms;
`;
const NavItems = styled.div``;
const NavItem = styled.p<{ $active: boolean }>`
  padding: 5px 0 5px 50px;
  line-height: 20px;
  font-family: "Hind";
  font-size: 14px;
  font-weight: ${theme.fontWeights.regular};
  color: ${theme.colors.text.grayLight};
  opacity: 0.45;
  transition-duration: 500ms;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }

  ${({ $active }) =>
    $active &&
    `
    opacity: 1;
  `}
`;

const Content = styled.div`
  padding: 0 0 0 120px;
  width: 100%;
  ${media.tablet`
     padding: 0 20px;
  `}
`;
const ContentItem = styled.div`
  margin: 0 0 130px 0;
`;
const ContentSubTitle = styled.p`
  font-family: "Hind";
  font-size: 20px;
  font-weight: ${theme.fontWeights.regular};
  color: ${theme.colors.text.default};
`;
const ContentTitle = styled.p`
  margin: 20px 0 30px 0;
  font-family: "Lato Bold", sans-serif;
  font-size: 30px;
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.primary};
`;
const ContentImg = styled.img`
  width: 100%;
  max-width: 1000px;
`;
const ContentDesc = styled.p`
  margin: 30px 0 0 0;
  line-height: 24px;
  font-family: "Hind";
  font-size: 14px;
  font-weight: ${theme.fontWeights.regular};
  color: ${theme.colors.text.grayLight};
`;
